// Pointer input → tick-quantised InputEvents.
//
// Three things here are easy to get wrong and expensive to notice late:
//
//  1. Tap-vs-drag. A tap is pointerdown → pointerup within a small movement threshold AND a short
//     time window. Without both checks, taps fire constantly during normal herding.
//  2. Bark is a DOUBLE tap — two taps close together in both time and space. A single tap is far
//     too easy to trigger while working the dog around the flock, which made barks fire by
//     accident constantly.
//  3. touch-action: none + pointer capture. Without them mobile scrolls, pinch-zooms, or drops the
//     pointer stream mid-run.

import type { InputEvent } from '../engine/types'
import type { CommandType } from '../engine/commands'

const TAP_MOVE_THRESHOLD_PX = 10
const TAP_TIME_THRESHOLD_MS = 250
/** Max gap between the two taps of a double tap. */
const DOUBLE_TAP_MS = 320
/** Both taps must land within this distance of each other, or it reads as two separate taps. */
const DOUBLE_TAP_DIST_PX = 40

export interface WorldMapper {
  /** Canvas client coords → world metres. */
  toWorld(clientX: number, clientY: number): { x: number; y: number }
}

export interface InputOptions {
  /**
   * The pre-P3 direct-control scheme: drag the dog, double-tap to bark.
   *
   * **Off by default, and retained rather than deleted on purpose.** P3 chose handler commands on a
   * system nobody has played yet; if commands turn out to feel remote, the fallback is this flag
   * rather than rebuilding the input layer under time pressure. The engine still consumes `move`
   * and `bark` identically either way, and the bot drives through them, so this code stays exercised
   * by the solvability gate even while no player can reach it. See PLAN.md § Playtest feedback P3.
   */
  direct?: boolean
}

export class InputRecorder {
  /** The full log, for replay verification. */
  readonly events: InputEvent[] = []
  readonly direct: boolean
  private downX = 0
  private downY = 0
  private downAt = 0
  private moved = false
  private currentTick = () => 0
  private dogAt = () => ({ x: 0, y: 0 })
  private lastTapAt = 0
  private lastTapX = 0
  private lastTapY = 0

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly mapper: WorldMapper,
    options: InputOptions = {},
  ) {
    // Required, and not achievable from CSS alone on all browsers. Kept even without direct
    // control: the canvas must not scroll or pinch-zoom under a thumb mid-run either way.
    canvas.style.touchAction = 'none'
    // Stop iOS long-press selection / context menu over the paddock, likewise regardless.
    canvas.addEventListener('contextmenu', (e) => e.preventDefault())

    this.direct = options.direct ?? false
    if (!this.direct) return

    canvas.addEventListener('pointerdown', this.onDown)
    canvas.addEventListener('pointermove', this.onMove)
    canvas.addEventListener('pointerup', this.onUp)
    canvas.addEventListener('pointercancel', this.onUp)
  }

  bindTickSource(fn: () => number): void {
    this.currentTick = fn
  }

  /**
   * Where the dog is, for barks issued from the command bar.
   *
   * A bark event carries coordinates because every input event does, and the engine honours them
   * when no standing order is active — so a bark logged at (0,0) before the first command would
   * send the dog to the corner of the paddock. Reporting the dog's own position makes the event
   * both harmless and truthful. Mirrors `bindTickSource` deliberately.
   */
  bindDogSource(fn: () => { x: number; y: number }): void {
    this.dogAt = fn
  }

  /** A handler command from the bar. Tick-quantised like any other input, so replay is unaffected. */
  command(type: CommandType): void {
    this.events.push({ tick: this.currentTick(), type, x: 0, y: 0 })
  }

  /** A bark from the bar, logged where the dog actually is. */
  bark(): void {
    const d = this.dogAt()
    this.events.push({ tick: this.currentTick(), type: 'bark', x: d.x, y: d.y })
  }

  /**
   * Log a pause or resume. The sim ignores both — see `InputEventType`.
   *
   * Coordinates are meaningless here and are recorded as zero. Both land on the same tick when a
   * pause is short, which is correct: no world time passed between them, because that is what a
   * pause is.
   */
  pause(): void {
    this.events.push({ tick: this.currentTick(), type: 'pause', x: 0, y: 0 })
  }

  resume(): void {
    this.events.push({ tick: this.currentTick(), type: 'resume', x: 0, y: 0 })
  }

  private record(type: InputEvent['type'], clientX: number, clientY: number): void {
    const { x, y } = this.mapper.toWorld(clientX, clientY)
    this.events.push({ tick: this.currentTick(), type, x, y })
  }

  private onDown = (e: PointerEvent): void => {
    this.canvas.setPointerCapture(e.pointerId)
    this.downX = e.clientX
    this.downY = e.clientY
    this.downAt = performance.now()
    this.moved = false
    this.record('move', e.clientX, e.clientY)
  }

  private onMove = (e: PointerEvent): void => {
    if (!this.canvas.hasPointerCapture(e.pointerId)) return
    const dx = e.clientX - this.downX
    const dy = e.clientY - this.downY
    if (dx * dx + dy * dy > TAP_MOVE_THRESHOLD_PX * TAP_MOVE_THRESHOLD_PX) this.moved = true
    this.record('move', e.clientX, e.clientY)
  }

  private onUp = (e: PointerEvent): void => {
    if (this.canvas.hasPointerCapture(e.pointerId)) {
      this.canvas.releasePointerCapture(e.pointerId)
    }
    const now = performance.now()
    const quick = now - this.downAt < TAP_TIME_THRESHOLD_MS
    if (this.moved || !quick) return

    // A completed tap. Bark only if it pairs with a recent tap nearby.
    const dt = now - this.lastTapAt
    const dx = e.clientX - this.lastTapX
    const dy = e.clientY - this.lastTapY
    const near = dx * dx + dy * dy <= DOUBLE_TAP_DIST_PX * DOUBLE_TAP_DIST_PX

    if (dt < DOUBLE_TAP_MS && near) {
      this.record('bark', e.clientX, e.clientY)
      // Reset so a third tap starts a fresh pair rather than barking again immediately.
      this.lastTapAt = 0
    } else {
      this.lastTapAt = now
      this.lastTapX = e.clientX
      this.lastTapY = e.clientY
    }
  }

  /** Events recorded for a given tick. The sim consumes these on the tick boundary. */
  drain(tick: number): InputEvent[] {
    const out: InputEvent[] = []
    for (let i = this.events.length - 1; i >= 0; i--) {
      if (this.events[i].tick === tick) out.unshift(this.events[i])
      else if (this.events[i].tick < tick) break
    }
    return out
  }
}
